import React, { useState } from "react";
import { getAccessToken } from "../../../awsAuth";
import { getPlanByKey } from "./plans";
import "./Checkout.scss";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

/**
 * Checkout is the integration point for a future real payment provider —
 * it never collects or stores card number / CVV itself. Monobank's invoice
 * flow (server/routes/payments.js POST /create) works by redirecting the
 * browser to a hosted Monobank payment page, so there is no card form to
 * build here even once the real merchant token is configured: the "Pay"
 * button below already calls the real, already-hardened endpoint and will
 * start redirecting correctly the moment MONOBANK_TOKEN/MONOBANK_TOKEN_SANDBOX
 * is set in the backend env (see ROADMAP.md). Until then it fails closed
 * with a plain, honest message instead of pretending to succeed.
 *
 * The plan is never activated here or anywhere on the client — only a
 * signed Monobank webhook (server/routes/payments.js POST /webhook/monobank)
 * can flip a payment to "succeeded" and grant the plan.
 */
export default function Checkout({ planKey, onClose }) {
  const plan = getPlanByKey(planKey);
  const [status, setStatus] = useState("idle"); // idle | creating | error
  const [error, setError] = useState("");

  if (!plan) return null;

  const handlePay = async () => {
    setStatus("creating");
    setError("");

    try {
      const token = await getAccessToken?.().catch(() => null);

      if (!token) {
        setError("Увійдіть у акаунт, щоб оформити підписку.");
        setStatus("error");
        return;
      }

      const res = await fetch(`${API_BASE}/api/payments/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          purpose: "subscription",
          description: `Підписка ${plan.name}`,
          metadata: { plan: plan.key },
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.redirectUrl) {
        throw new Error(data?.error || "no redirectUrl");
      }

      // Real flow: browser leaves Cvanto and lands on Monobank's hosted
      // payment page. The plan activates only after Monobank calls our
      // signed webhook back — never on this redirect itself.
      window.location.href = data.redirectUrl;
    } catch (e) {
      setError(
        "Оплата тимчасово недоступна — підключення платіжного провайдера ще завершується. Спробуйте пізніше."
      );
      setStatus("error");
    }
  };

  return (
    <div className="checkout-overlay" onMouseDown={onClose}>
      <div className="checkout-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="checkout-close" onClick={onClose} aria-label="Закрити">
          ✕
        </button>

        <div className="checkout-header">
          <div className="checkout-eyebrow">Оформлення підписки</div>
          <h2>{plan.name}</h2>
        </div>

        <div className="checkout-summary">
          <div className="checkout-row">
            <span>Тариф</span>
            <span>{plan.name}</span>
          </div>
          <div className="checkout-row">
            <span>Період</span>
            <span>Щомісяця</span>
          </div>
          <div className="checkout-row checkout-row--total">
            <span>До сплати</span>
            <span>{plan.price}</span>
          </div>
        </div>

        <ul className="checkout-features">
          {plan.features.slice(0, 5).map((feature) => (
            <li key={feature}>
              <span>✓</span>
              {feature}
            </li>
          ))}
        </ul>

        <div className="checkout-payment-box">
          <div className="checkout-payment-title">Спосіб оплати</div>
          <p>
            Оплата відбувається на захищеній сторінці Monobank — номер картки
            і CVV ви вводите там. Cvanto їх ніколи не бачить і не зберігає.
          </p>
        </div>

        {status === "error" && <div className="checkout-error">{error}</div>}

        <button
          type="button"
          className="checkout-pay-btn"
          onClick={handlePay}
          disabled={status === "creating"}
        >
          {status === "creating" ? "Створюємо платіж…" : `Оплатити ${plan.price}`}
        </button>

        <p className="checkout-fineprint">
          Підписку буде активовано автоматично одразу після підтвердження
          оплати банком. Тариф ніколи не активується без реальної оплати.
        </p>
      </div>
    </div>
  );
}
